/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useSelector } from 'react-redux';
import { List, Loader } from 'semantic-ui-react';
import { Table } from '../../../../components';
import { map, isEmpty, get } from 'lodash';
import Fuse from 'fuse.js';
import { FormattedMessage } from 'react-intl';
import EditManufacturerButton from './EditManufacturerButton';
import DeleteManufacturerButton from './DeleteManufacturerButton';

import type { Manufacturer } from '../../../../type';

// Query for list of manufacturers of current salon
export const GET_MANUFACTURERS_QUERY = gql`
    query getManufacturers($salon: ID!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    manufacturers {
                        pageInfo {
                            hasNextPage
                            endCursor
                        }
                        nodes {
                            id
                            name
                        }
                    }
                }
            }
        }
    }
`;

type ManufacturersListProps = {
    manufacturers: Array<Manufacturer>,
};

type ManufacturerRowProps = {
    manufacturer: Manufacturer,
};

/**
 * Show this message if inventory list is empty
 */
const EmptyMessage = () => (
    <Table.Row className="transparent">
        <Table.Cell colSpan={2} textAlign="center">
            <FormattedMessage
                id="pages.settings.manufacturers.noManufacturers"
                defaultMessage="You don't have manufacturers yet"
            />
        </Table.Cell>
    </Table.Row>
);

/**
 * The component represents single manufacturer row with name and actions
 */
const ManufacturerRow = ({ manufacturer }: ManufacturerRowProps) => (
    <Table.Row>
        <Table.Cell>{manufacturer.name}</Table.Cell>
        <Table.Cell>
            <List horizontal divided>
                <List.Item>
                    <EditManufacturerButton manufacturer={manufacturer} />
                </List.Item>
                <List.Item>
                    <DeleteManufacturerButton manufacturer={manufacturer} />
                </List.Item>
            </List>{' '}
        </Table.Cell>
    </Table.Row>
);

/**
 * Renders list of manufacturers
 */
const ManufacturersList = () => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const searchQuery = useSelector(({ manufacturers }) =>
        manufacturers.get('searchQuery'),
    );

    const { loading, data } = useQuery(GET_MANUFACTURERS_QUERY, {
        variables: { salon },
        fetchPolicy: 'cache-and-network',
        skip: !salon,
    });

    if (loading) {
        return <Loader size="large" inline="centered" active />;
    }

    let manufacturers = get(data, 'viewer.salon.manufacturers.nodes', []);
    if (searchQuery) {
        const fuse = new Fuse(manufacturers, {
            keys: ['name'],
            shouldSort: true,
            threshold: 0.3,
        });
        manufacturers = fuse.search(searchQuery);
    }

    return (
    <Table celled selectable striped fixed>
        <Table.Header>
            <Table.Row>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.settings.manufacturers.name"
                        defaultMessage="Name"
                    />
                </Table.HeaderCell>
                <Table.HeaderCell>
                    <FormattedMessage
                        id="pages.settings.manufacturers.actions"
                        defaultMessage="Actions"
                    />
                </Table.HeaderCell>
            </Table.Row>
        </Table.Header>
        <Table.Body>
            {isEmpty(manufacturers) ? (
                <EmptyMessage />
            ) : (
                map(manufacturers, manufacturer => (
                    <ManufacturerRow
                        key={manufacturer.id}
                        manufacturer={manufacturer}
                    />
                ))
            )}
        </Table.Body>
    </Table>
    );
};

export default ManufacturersList;
